import * as THREE from 'three';
import { GUARDIAN_POSITION } from './guardianScene';

export const REGULAR_ATTACK_STYLES = {
  wisp: { count: 3, form: 'plasma', spread: 0.8, spin: 2.2 },
  wing: { count: 5, form: 'feather', spread: 1.8, spin: 0.3 },
  maw: { count: 4, form: 'fang', spread: 1.6, spin: 0.2 },
  hound: { count: 3, form: 'claw', spread: 1.2, spin: 0.1 },
  sentry: { count: 3, form: 'gate', spread: 1.5, spin: 0.8 },
  serpent: { count: 3, form: 'serpent', spread: 1.1, spin: 1.3 },
  spider: { count: 7, form: 'needle', spread: 2, spin: 0.25 },
  reaper: { count: 1, form: 'scythe', spread: 2.6, spin: 1.8 },
  knight: { count: 1, form: 'lance', spread: 0, spin: 3 },
};

const glow = (color, opacity = 0.8) =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });

const projectileGeometry = (form) => {
  if (form === 'gate') return new THREE.TorusGeometry(0.5, 0.055, 5, 6);
  if (form === 'scythe')
    return new THREE.RingGeometry(0.62, 0.87, 32, 1, 0.1, Math.PI * 1.35);
  if (['fang', 'needle', 'lance'].includes(form))
    return new THREE.ConeGeometry(
      form === 'lance' ? 0.18 : 0.12,
      form === 'needle' ? 1.45 : 1.9,
      5
    );
  if (form === 'plasma' || form === 'serpent')
    return new THREE.IcosahedronGeometry(form === 'plasma' ? 0.23 : 0.19, 1);
  const shape = new THREE.Shape();
  shape.moveTo(0, 1.1);
  [
    [-0.35, -0.45],
    [-0.15, -0.25],
    [-0.12, -0.9],
    [0.18, -0.42],
    [0.35, -0.55],
    [0, 1.1],
  ].forEach(([x, y]) => shape.lineTo(x, y));
  return new THREE.ExtrudeGeometry(shape, { depth: 0.07, bevelEnabled: false });
};

// Visuals only: the existing combat state owns damage and the single hit time.
export const createRegularEnemyAttackScene = (scene) => {
  const group = new THREE.Group();
  group.name = 'regular-enemy-counterattack';
  group.visible = false;
  scene.add(group);
  const charge = new THREE.Group();
  const flightGroup = new THREE.Group();
  const impact = new THREE.Group();
  group.add(charge, flightGroup, impact);
  const material = glow('#79f5e6');
  const hot = glow('#f3ffff', 0.9);
  const shell = new THREE.MeshStandardMaterial({
    color: '#263b48',
    emissive: '#79f5e6',
    emissiveIntensity: 0.8,
    metalness: 0.72,
    roughness: 0.28,
    flatShading: true,
    side: THREE.DoubleSide,
  });
  const edgeMaterial = new THREE.LineBasicMaterial({
    color: '#79f5e6',
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    toneMapped: false,
  });
  const rings = [0, 1, 2].map((index) => {
    const mesh = new THREE.Mesh(
      new THREE.TorusGeometry(0.7 + index * 0.23, 0.022, 5, 48),
      material
    );
    charge.add(mesh);
    return mesh;
  });
  const projectiles = Array.from({ length: 7 }, () => {
    const root = new THREE.Group();
    const head = new THREE.Mesh(new THREE.IcosahedronGeometry(0.2, 1), shell);
    const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.1, 0), hot);
    const edge = new THREE.LineSegments(
      new THREE.EdgesGeometry(head.geometry),
      edgeMaterial
    );
    head.add(edge);
    const shaft = new THREE.Mesh(
      new THREE.CylinderGeometry(0.035, 0.07, 2.6, 6),
      shell
    );
    shaft.position.y = -1.35;
    shaft.visible = false;
    root.add(head, core, shaft);
    flightGroup.add(root);
    const positions = new THREE.BufferAttribute(
      new Float32Array(25 * 2 * 3),
      3
    );
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', positions);
    const indices = [];
    for (let index = 0; index < 24; index++) {
      const a = index * 2;
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    geometry.setIndex(indices);
    const trail = new THREE.Mesh(
      geometry,
      new THREE.MeshBasicMaterial({
        color: '#79f5e6',
        transparent: true,
        opacity: 0.65,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
        side: THREE.DoubleSide,
      })
    );
    trail.frustumCulled = false;
    group.add(trail);
    return { root, head, edge, shaft, core, trail, positions };
  });
  const impactRings = [0, 1, 2].map((index) => {
    const mesh = new THREE.Mesh(
      new THREE.TorusGeometry(0.65 + index * 0.15, 0.025, 4, 48),
      material
    );
    impact.add(mesh);
    return mesh;
  });
  const crackPositions = new THREE.BufferAttribute(new Float32Array(18 * 6), 3);
  const crackGeometry = new THREE.BufferGeometry();
  crackGeometry.setAttribute('position', crackPositions);
  const cracks = new THREE.LineSegments(
    crackGeometry,
    new THREE.LineBasicMaterial({
      color: '#79f5e6',
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    })
  );
  cracks.frustumCulled = false;
  impact.add(cracks);
  const origin = GUARDIAN_POSITION.clone().add(new THREE.Vector3(0, 0.3, 2.4));
  const forward = new THREE.Vector3(),
    right = new THREE.Vector3(),
    up = new THREE.Vector3();
  const point = new THREE.Vector3(),
    tail = new THREE.Vector3(),
    worldUp = new THREE.Vector3(0, 1, 0);
  let previousShape;
  const clamp = THREE.MathUtils.clamp;

  const update = ({
    enemy,
    combat,
    elapsed,
    target,
    camera,
    reduceMotion = false,
  }) => {
    group.visible =
      !!combat?.retaliation &&
      elapsed >= combat.counterWindupAt &&
      elapsed < combat.counterAt + 700;
    if (!group.visible) return;
    const shape = REGULAR_ATTACK_STYLES[enemy.shape] ? enemy.shape : 'wisp';
    const style = REGULAR_ATTACK_STYLES[shape];
    group.userData.attackStyle = style.form;
    if (previousShape !== shape) {
      previousShape = shape;
      projectiles.forEach(({ head, edge }) => {
        head.geometry.dispose();
        head.geometry = projectileGeometry(style.form);
        edge.geometry.dispose();
        edge.geometry = new THREE.EdgesGeometry(head.geometry);
      });
    }
    const charging = elapsed < combat.counterLaunchAt;
    const traveling = !charging && elapsed < combat.counterAt;
    const p = clamp(
      (elapsed - combat.counterLaunchAt) /
        Math.max(1, combat.counterAt - combat.counterLaunchAt),
      0,
      1
    );
    const q = clamp(
      (elapsed - combat.counterWindupAt) /
        Math.max(1, combat.counterLaunchAt - combat.counterWindupAt),
      0,
      1
    );
    const hit = clamp((elapsed - combat.counterAt) / 700, 0, 1);
    const time = reduceMotion ? 0 : elapsed / 1000;
    const color = combat.damage === 0 && !charging ? '#8effe4' : enemy.color;
    material.color.set(color);
    shell.color.set(enemy.color).multiplyScalar(0.3);
    shell.emissive.set(enemy.color);
    edgeMaterial.color.set(enemy.color);
    material.opacity = charging ? 0.35 + q * 0.5 : 0.85 * (1 - hit);
    charge.visible = charging;
    charge.position.copy(origin);
    charge.quaternion.copy(camera.quaternion);
    rings.forEach((ring, index) => {
      ring.visible = !reduceMotion || index === 0;
      ring.rotation.set(
        shape === 'sentry' ? index * 0.8 : 0,
        shape === 'wisp' ? index * 0.7 : 0,
        time * style.spin * (index % 2 ? -1 : 1)
      );
      ring.scale.setScalar((1.5 - q * 0.5) * (shape === 'knight' ? 0.75 : 1));
    });
    forward.subVectors(target, origin).normalize();
    right.crossVectors(forward, worldUp).normalize();
    up.crossVectors(right, forward).normalize();
    const pathPoint = (t, index, out) => {
      out.lerpVectors(origin, target, t);
      const envelope = Math.sin(t * Math.PI);
      const angle = (index / style.count) * Math.PI * 2;
      let x = Math.cos(angle) * style.spread * envelope,
        y = Math.sin(angle) * style.spread * envelope;
      if (shape === 'wisp' || shape === 'serpent') {
        x =
          Math.sin(t * (shape === 'wisp' ? 16 : 10) + angle) *
          style.spread *
          envelope;
        y = Math.cos(t * 10 + angle) * style.spread * envelope;
      }
      if (shape === 'wing') {
        x = (index - 2) * envelope * 0.85;
        y = envelope * (1.6 - Math.abs(index - 2) * 0.3);
      }
      if (shape === 'maw') {
        x = (index % 2 ? -1 : 1) * envelope * 1.4;
        y = (index < 2 ? 1 : -1) * envelope * 0.7;
      }
      if (shape === 'hound') {
        x = (index - 1) * envelope * 0.4 + envelope * 1.3;
        y = (index - 1) * envelope * 0.5;
      }
      if (shape === 'reaper') {
        x = envelope * 2.6;
        y = envelope * 0.6;
      }
      out.addScaledVector(right, x).addScaledVector(up, y);
      return out;
    };
    flightGroup.visible = charging || traveling;
    projectiles.forEach(
      ({ root, head, shaft, core, trail, positions }, index) => {
        root.visible = index < style.count && (!reduceMotion || charging);
        trail.visible = index < style.count && traveling && !reduceMotion;
        if (!root.visible && !trail.visible) return;
        if (charging) {
          const angle = (index / style.count) * Math.PI * 2 + time * style.spin;
          root.position
            .copy(origin)
            .addScaledVector(right, Math.cos(angle) * (1.6 - q * 0.7))
            .addScaledVector(up, Math.sin(angle) * (1.6 - q * 0.7));
        } else root.position.copy(pathPoint(p, index, point));
        root.quaternion.copy(camera.quaternion);
        root.rotateZ(
          shape === 'hound'
            ? -0.6
            : shape === 'wing'
            ? (index - 2) * -0.2
            : time * style.spin
        );
        if (['needle', 'fang', 'lance'].includes(style.form)) {
          root.lookAt(target);
          root.rotateX(Math.PI / 2);
        }
        head.scale.setScalar(style.form === 'scythe' ? 1.7 : 1);
        shaft.visible = style.form === 'lance';
        core.scale.setScalar(style.form === 'lance' ? 1.4 : 1);
        trail.material.color.set(enemy.color);
        for (let segment = 0; segment <= 24; segment++) {
          pathPoint(
            Math.max(
              0,
              p - (1 - segment / 24) * (shape === 'serpent' ? 0.45 : 0.22)
            ),
            index,
            tail
          );
          const width =
            0.01 + (segment / 24) * (shape === 'serpent' ? 0.13 : 0.055);
          positions.setXYZ(
            segment * 2,
            tail.x - right.x * width,
            tail.y - right.y * width,
            tail.z - right.z * width
          );
          positions.setXYZ(
            segment * 2 + 1,
            tail.x + right.x * width,
            tail.y + right.y * width,
            tail.z + right.z * width
          );
        }
        positions.needsUpdate = true;
      }
    );
    impact.visible = elapsed >= combat.counterAt;
    if (impact.visible) {
      impact.position.copy(target);
      impact.quaternion.copy(camera.quaternion);
      impactRings.forEach((ring, index) => {
        ring.visible = !reduceMotion || index === 0;
        ring.scale.setScalar(
          reduceMotion
            ? 1
            : 0.3 + hit * (shape === 'sentry' ? 4 : 2.5) + index * 0.18
        );
        ring.rotation.set(
          0,
          shape === 'maw' ? index * 0.7 : 0,
          shape === 'reaper' ? index * 0.5 : time * 0.3
        );
      });
      cracks.visible = !reduceMotion;
      cracks.material.color.set(color);
      cracks.material.opacity = 1 - hit;
      for (let index = 0; index < 18; index++) {
        const angle = (index / 18) * Math.PI * 2;
        const radius = 0.4 + hit * 2;
        const x = Math.cos(angle) * radius,
          y = Math.sin(angle) * radius;
        if (shape === 'hound' || shape === 'reaper') {
          crackPositions.setXYZ(
            index * 2,
            ((index % 3) - 1) * 0.35 - radius,
            -radius,
            0
          );
          crackPositions.setXYZ(
            index * 2 + 1,
            ((index % 3) - 1) * 0.35 + radius,
            radius,
            0
          );
        } else {
          crackPositions.setXYZ(index * 2, x * 0.25, y * 0.25, 0);
          const end = shape === 'spider' ? angle + Math.PI / 3 : angle + 0.1;
          crackPositions.setXYZ(
            index * 2 + 1,
            Math.cos(end) * radius,
            Math.sin(end) * radius,
            0
          );
        }
      }
      crackPositions.needsUpdate = true;
    }
  };
  return {
    group,
    projectiles,
    update,
    dispose() {
      const geometries = new Set(),
        materials = new Set();
      group.traverse((object) => {
        if (object.geometry) geometries.add(object.geometry);
        if (object.material) materials.add(object.material);
      });
      group.removeFromParent();
      geometries.forEach((item) => item.dispose());
      materials.forEach((item) => item.dispose());
    },
  };
};
